/* ==========================================================================
   DF-WHATSAPP-BUTTON — self-contained behavior
   --------------------------------------------------------------------------
   Progressive enhancement only. The wa.me link in
   snippets/df-whatsapp-button.liquid already works with zero JavaScript.
   This file only:
     1) hides the button while the cart drawer or the privacy/cookie
        banner is open (so it never sits on top of a focus-trapped dialog)
     2) lifts the button above the mobile product sticky-bar
     3) on desktop only, skips wa.me's own interstitial redirect page and
        opens web.whatsapp.com directly (same destination, one less hop)
     4) exposes a small, documented extension point for future features
        (business hours, offline message, department routing) without
        requiring any changes to the Liquid snippet or the CSS file.

   Deliberately NOT done, and why:
   - No whatsapp:// custom-scheme handling. It cannot fail gracefully
     (no app installed → broken/blank navigation or a browser alert) and
     is blocked outright inside most in-app browsers (Instagram, Facebook,
     TikTok, LinkedIn), leaving visitors stuck with no fallback. wa.me
     already opens the WhatsApp app directly on iOS/Android via the OS's
     own universal-link mechanism when the app is installed, so there is
     nothing to gain here and real breakage to risk.

   No network requests are made from this file. No Meta/WhatsApp script,
   pixel, or SDK is loaded. Nothing runs on click except a plain browser
   navigation to a wa.me or web.whatsapp.com URL already present in the
   HTML or derived from it.
   ========================================================================== */

(function () {
  'use strict';

  var button = document.querySelector('[data-df-whatsapp-button]');
  if (!button) return;

  var HIDDEN_CLASS = 'df-whatsapp-btn--hidden';
  var LIFTED_CLASS = 'df-whatsapp-btn--lifted';
  var MOBILE_QUERY = '(max-width: 699px)';

  var cartDrawer = document.querySelector('cart-drawer');
  var privacyBanner = document.querySelector('privacy-banner');
  var stickyBar = document.querySelector('.product-sticky-bar');

  function isDialogOpen(el) {
    return !!el && el.hasAttribute('open');
  }

  function updateHiddenState() {
    var shouldHide = isDialogOpen(cartDrawer) || isDialogOpen(privacyBanner);
    button.classList.toggle(HIDDEN_CLASS, shouldHide);
  }

  function updateLiftedState() {
    var isMobile = window.matchMedia(MOBILE_QUERY).matches;
    var stickyVisible = !!stickyBar && stickyBar.classList.contains('is-visible');
    button.classList.toggle(LIFTED_CLASS, isMobile && stickyVisible);
  }

  // --- Cart drawer / privacy banner open-state watcher ---------------------
  // Both components reflect their visibility via a boolean "open" attribute
  // (part of the theme's shared dialog behavior), so we only ever watch
  // that one attribute rather than any internal class names.
  var dialogObserver = new MutationObserver(updateHiddenState);

  [cartDrawer, privacyBanner].forEach(function (el) {
    if (!el) return;
    dialogObserver.observe(el, { attributes: true, attributeFilter: ['open'] });
  });

  // --- Mobile sticky bar watcher --------------------------------------------
  if (stickyBar) {
    var stickyObserver = new MutationObserver(updateLiftedState);
    stickyObserver.observe(stickyBar, { attributes: true, attributeFilter: ['class'] });

    if (window.matchMedia) {
      var mobileMql = window.matchMedia(MOBILE_QUERY);
      var onMqlChange = function () { updateLiftedState(); };
      if (mobileMql.addEventListener) {
        mobileMql.addEventListener('change', onMqlChange);
      } else if (mobileMql.addListener) {
        // Safari < 14 fallback
        mobileMql.addListener(onMqlChange);
      }
    }
  }

  // Initial state on load.
  updateHiddenState();
  updateLiftedState();

  // ==========================================================================
  // Desktop shortcut: web.whatsapp.com instead of wa.me's interstitial.
  // --------------------------------------------------------------------------
  // "Desktop" here is simply the theme's own 700px breakpoint (same one used
  // above for the sticky bar), not device/browser sniffing — kept simple on
  // purpose. Worst case on a misclassified device: the visitor lands on
  // web.whatsapp.com instead of opening the app directly, which still works,
  // it just needs an existing WhatsApp Web session. Nothing breaks.
  //
  // If anything here fails for any reason (URL API missing, malformed href,
  // etc.) the function simply returns null and the click proceeds as a
  // completely normal wa.me link — no error is possible for the visitor.
  // ==========================================================================
  function isDesktopViewport() {
    return window.matchMedia && !window.matchMedia(MOBILE_QUERY).matches;
  }

  function buildWebWhatsAppUrl(waMeHref) {
    try {
      var waMeUrl = new URL(waMeHref);
      var phone = waMeUrl.pathname.replace(/^\/+/, '');
      var text = waMeUrl.searchParams.get('text') || '';
      if (!phone) return null;

      var webUrl = new URL('https://web.whatsapp.com/send');
      webUrl.searchParams.set('phone', phone);
      webUrl.searchParams.set('text', text);
      return webUrl.toString();
    } catch (err) {
      return null;
    }
  }

  // ==========================================================================
  // EXTENSION POINT — do not remove.
  // --------------------------------------------------------------------------
  // Future features (business hours, offline message, department routing)
  // can hook in here without touching the Liquid snippet, the CSS file, or
  // anything above this comment. Example of a future addition:
  //
  //   window.DFWhatsAppHooks.beforeOpen = function (anchorEl) {
  //     var hour = new Date().getHours();
  //     if (hour < 9 || hour >= 18) {
  //       anchorEl.href = anchorEl.href.replace(/text=[^&]*/, 'text=' +
  //         encodeURIComponent('Wir sind gerade nicht erreichbar...'));
  //     }
  //   };
  //
  // beforeOpen (if defined) is called with the button element right before
  // the browser follows the link. It must not call preventDefault(); it may
  // only rewrite attributes on the element passed to it.
  // ==========================================================================
  window.DFWhatsAppHooks = window.DFWhatsAppHooks || {};

  button.addEventListener('click', function (event) {
    if (typeof window.DFWhatsAppHooks.beforeOpen === 'function') {
      try {
        window.DFWhatsAppHooks.beforeOpen(button);
      } catch (err) {
        // A future hook failing must never block the contact link itself.
        if (window.console && window.console.warn) {
          console.warn('DFWhatsAppHooks.beforeOpen failed:', err);
        }
      }
    }

    if (!isDesktopViewport()) return; // mobile: leave the native wa.me link untouched

    var webUrl = buildWebWhatsAppUrl(button.getAttribute('href'));
    if (!webUrl) return; // anything unexpected: fall back to the plain wa.me click

    event.preventDefault();
    window.open(webUrl, '_blank', 'noopener,noreferrer');
  });
})();

